import { useEffect } from "react";
import { Button } from "@/components/ui";
import { IngredientManager } from "@/components/common";
import { useVariantIngredients } from "../api";

interface VariantIngredientEditorProps {
  variantId: string;
  variantName: string;
  variantSku: string;
  ingredientsList: { id: string; name: string; unit: string }[];
  current: { ingredientId: string; quantityUsed: number }[] | undefined;
  onChange: (ingredients: { ingredientId: string; quantityUsed: number }[]) => void;
  onSave: () => void;
  saving: boolean;
}

export function VariantIngredientEditor({
  variantId,
  variantName,
  variantSku,
  ingredientsList,
  current,
  onChange,
  onSave,
  saving,
}: VariantIngredientEditorProps) {
  const { data: serverRows, isFetching } = useVariantIngredients(variantId);

  useEffect(() => {
    if (current !== undefined) {
      return;
    }

    if (!serverRows) {
      return;
    }

    onChange(
      serverRows.map((row) => ({
        ingredientId: row.ingredientId,
        quantityUsed: row.quantityUsed,
      })),
    );
  }, [serverRows]);

  return (
    <div className="rounded-md border p-4">
      <div className="mb-3 flex items-center justify-between">
        <div>
          <h4 className="font-medium">{variantName}</h4>
          <p className="text-xs text-muted-foreground">{variantSku}</p>
        </div>
        <Button
          type="button"
          size="sm"
          onClick={onSave}
          disabled={saving || isFetching}
        >
          {saving ? "Saving…" : "Save"}
        </Button>
      </div>
      {isFetching && current === undefined ? (
        <p className="py-2 text-center text-xs text-muted-foreground">
          Loading ingredients…
        </p>
      ) : (
        <IngredientManager
          ingredients={current ?? []}
          availableIngredients={ingredientsList}
          onChange={onChange}
          title=""
          description=""
          compact
        />
      )}
    </div>
  );
}
